<template>
  <v-container fluid fill-height ma-0 pa-1 >
    <v-layout fill-height v-if="devices_by_typeShow" justify-start>
        <v-flex xs3>

          <scroll-table
            :data="statistics.table.devicesTable"
            :options="options"
            :translate="true"
            :toolbar="'devices'"
          />

        </v-flex>
        <v-flex xs12 ml-1>

          <scroll-table
            :data="getTable"
            :options="options"
            :sortable="true"
            :sorted="sort"
            :toolbar="'cabinets'"
            @select="(row) => onCabinetOpen(row)"
            @sort="(item) => sortTable(item)"
          />

        </v-flex>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import scrollTable from '@/nms/components/partials/scrolltable.vue'

// Типы
interface DeviceItem {
  $id?: string
  id?: string
  name?: string
  cls?: string
  device_type?: string
  consume?: number
  height?: number
  position?: number
  allocated_power?: number
  usize?: number
  children?: DeviceItem[]
  [key: string]: any
}

interface CabinetData {
  allocatedPower?: number
  devicesPower: number
  devicesUnits: number
  usize: number
  devices: DeviceItem[]
  obj: DeviceItem
  [key: string]: any
}

interface StatisticsData {
  fullLinks?: DeviceItem[]
  devices_by_type?: Record<string, DeviceItem[]>
  cabinet?: Record<string, CabinetData>
  table?: {
    devicesTable?: TableData
    cabinetTable?: TableData
  }
}

interface TableColumn {
  title: string
  value: string
  type: string
  width: string
}

interface TableRow {
  device_type?: string
  summ?: number
  name?: string
  $id?: string
  namePath?: string
  allocatedPower?: number
  devicesPower?: number
  available_power?: number
  usize?: number
  devicesUnits?: number
  available_units?: number
  [key: string]: any
}

interface TableData {
  columns: TableColumn[]
  rows: TableRow[]
}

interface OptionsColumns {
  [key: string]: { width: string }
}

interface OptionsData {
  columns: OptionsColumns
}

interface ActiveTreeItem {
  $id?: string
  [key: string]: any
}

interface NamePathResponse {
  namePath?: string
  [key: string]: any
}

export default Vue.extend({
  name: 'LOCATION_STATISTICS',

  components: {
    scrollTable,
  },

  props: {},

  data(): {
    devices_by_typeShow: boolean
    statistics: StatisticsData
    options: OptionsData
    filter: any | null
    search: string
    sort: string
  } {
    return {
      devices_by_typeShow: false,
      statistics: {},
      options: {
        columns: {
          text: { width: '40%' },
          timestamp: { width: '40%' },
          actions: { width: '40%' },
          endpoint: { width: '40%' },
          check: { width: '20%' },
        },
      },
      filter: null,
      search: '',
      sort: '',
    }
  },

  computed: {
    getTable(): TableData | undefined {
      const cabinetTable = this.statistics.table?.cabinetTable
      if (!cabinetTable) return undefined
      
      const t = this.$merge({}, cabinetTable) as TableData
      
      if (this.search.length) {
        const searchLower = this.search.toLowerCase()
        t.rows = t.rows.filter(row => {
          return (row.name && row.name.toLowerCase().indexOf(searchLower) > -1) ||
                 (row.namePath && row.namePath.toLowerCase().indexOf(searchLower) > -1)
        })
      }
      
      if (this.sort) {
        const isDescending = this.sort.charAt(0) === '-'
        const fieldName = isDescending ? this.sort.slice(1) : this.sort
        const direction = isDescending ? -1 : 1
        
        t.rows = [...t.rows].sort(this.$compare(fieldName, direction))
      }
      
      return t
    },
  },

  watch: {
    '$store.state.devices.treeActive': {
      handler(newVal: ActiveTreeItem[]): void {
        if (!newVal) return
        this.setup()
      },
      deep: true
    },
  },

  created(): void {
    this.setup()
  },

  methods: {
    setup(): void {
      this.loadData()
    },

    async loadData(): Promise<void> {
      this.setLoader('loadData', true)
      
      const activeTree = this.$store.state.devices.treeActive as ActiveTreeItem[]
      const obj$ = await this.$bridge.getObjectById(activeTree[0]?.$id)
      console.log('LOCATION_STATISTICS', 'TEST', 'treeActive', obj$)
      
      const data$ = await obj$.getLinks?.({ compute: 'is_used' })
      this.$set(this.statistics, 'fullLinks', data$)
      console.log('LOCATION_STATISTICS', 'TEST', 'statistics', this.statistics.fullLinks)
      
      const devices_by_type: Record<string, DeviceItem[]> = {}
      const cabinet: Record<string, CabinetData> = {}
      
      const cycle = async (arr: DeviceItem[], cabinetName?: string): Promise<void> => {
        const c$ = cabinetName
        const cUnits = new Set<number>()
        
        for (const child of arr) {
          if (child.cls === 'cabinet') {
            if (!cabinet[child.name || '']) {
              this.$set(cabinet, child.name || '', {})
            }
            this.$set(cabinet[child.name || ''], 'allocatedPower', child.allocated_power || 0)
            this.$set(cabinet[child.name || ''], 'devicesPower', 0)
            this.$set(cabinet[child.name || ''], 'devicesUnits', 0)
            this.$set(cabinet[child.name || ''], 'usize', child.usize || 0)
            this.$set(cabinet[child.name || ''], 'devices', [])
            this.$set(cabinet[child.name || ''], 'obj', child)
          }
          
          if (child.cls === 'device') {
            const deviceType = child.device_type || 'unknown'
            if (!devices_by_type[deviceType]) {
              devices_by_type[deviceType] = []
            }
            devices_by_type[deviceType].push(child)
            
            if (c$) {
              cabinet[c$].devices.push(child)
              if (child.consume) {
                cabinet[c$].devicesPower = (cabinet[c$].devicesPower || 0) + child.consume
              }
              if (child.height && child.position) {
                const position = Number(child.position)
                if (!cUnits.has(position)) {
                  const length = Math.ceil(child.height / 45)
                  for (let i = 0; i < length; i++) {
                    cUnits.add(position - i)
                  }
                }
              }
              cabinet[c$].devicesUnits = cUnits.size
            }
          }
          
          if (child.children?.length) {
            if (child.cls === 'cabinet') {
              await cycle(child.children, child.name)
            } else {
              await cycle(child.children)
            }
          }
        }
      }
      
      if (this.statistics.fullLinks) {
        await cycle(this.statistics.fullLinks)
      }
      
      await this.$set(this.statistics, 'devices_by_type', devices_by_type)
      await this.$set(this.statistics, 'cabinet', cabinet)
      
      this.setLoader('loadData', false)
      this.createStatisticsTable()
    },

    async createStatisticsTable(): Promise<void> {
      this.$set(this.statistics, 'table', {})
      
      const devicesTable: TableData = {
        columns: [
          {
            title: 'device_type',
            value: 'device_type',
            type: 'text',
            width: '80%'
          },
          {
            title: 'summ',
            value: 'summ',
            type: 'text',
            width: '20%'
          }
        ],
        rows: []
      }
      
      const cabinetTable: TableData = {
        columns: [
          { title: 'name', value: 'name', type: 'text', width: '20%' },
          { title: 'name path', value: 'namePath', type: 'text', width: '35%' },
          { title: 'allocated power', value: 'allocatedPower', type: 'text', width: '10%' },
          { title: 'consume', value: 'devicesPower', type: 'text', width: '10%' },
          { title: 'available power', value: 'available_power', type: 'text', width: '10%' },
          { title: 'usize', value: 'usize', type: 'text', width: '10%' },
          { title: 'busy units', value: 'devicesUnits', type: 'text', width: '10%' },
          { title: 'available units', value: 'available_units', type: 'text', width: '10%' }
        ],
        rows: []
      }
      
      const dbtKeys = Object.keys(this.statistics.devices_by_type || {})
      if (dbtKeys.length) {
        dbtKeys.forEach(key => {
          devicesTable.rows.push({
            device_type: key,
            summ: this.statistics.devices_by_type?.[key]?.length || 0
          })
        })
      }
      
      this.$set(this.statistics.table, 'devicesTable', devicesTable)
      
      const cbntKeys = Object.keys(this.statistics.cabinet || {})
      if (cbntKeys.length) {
        for (const key of cbntKeys) {
          const cabinetData = this.statistics.cabinet?.[key]
          if (!cabinetData) continue
          
          const row: TableRow = {
            name: key,
            $id: cabinetData.obj?.$id,
            namePath: await this.loadNamePath(cabinetData.obj?.$id),
            allocatedPower: cabinetData.allocatedPower,
            devicesPower: cabinetData.devicesPower?.toFixed(2),
            usize: cabinetData.usize || 0,
            devicesUnits: cabinetData.devicesUnits,
          }
          
          const allocatedPower = row.allocatedPower || 0
          const devicesPower = parseFloat(row.devicesPower as string) || 0
          row.available_power = allocatedPower - devicesPower
          
          const usize = row.usize || 0
          const devicesUnits = row.devicesUnits || 0
          row.available_units = usize === 0 ? 0 : usize - devicesUnits
          
          cabinetTable.rows.push(row)
        }
      }
      
      this.$set(this.statistics.table, 'cabinetTable', cabinetTable)
      this.devices_by_typeShow = true
    },

    async loadNamePath($id?: string): Promise<string> {
      try {
        if (!$id) return ''
        
        const query = {
          id: [$id],
          data: { namePath: true }
        }
        const obj$ = await this.$bridge.cmdbQuery(query) as NamePathResponse[]
        
        if (obj$?.length && obj$[0].namePath) {
          const namePath = obj$[0].namePath
          const parts = namePath.split('/')
          return parts.slice(2).join('/')
        }
        return ''
      } catch (err) {
        console.error('CATCH', this.$options.name, 'loadNamePath', err)
        return ''
      }
    },

    async onCabinetOpen(row: TableRow): Promise<void> {
      try {
        console.log(this.$options.name, 'onCabinetOpen', 'row', row)
        const obj$ = await this.$bridge.getObjectById(row.$id)
        console.log(this.$options.name, 'onCabinetOpen', 'cabinet', this.statistics.cabinet?.[row.name || ''])
        
        if (obj$) {
          const dialogData = {
            component: 'statisticsModal',
            id: row.name,
            title: obj$.name,
            data: obj$,
            options: {
              width: 1400,
              height: 700,
              top: 150
            }
          }
          this.$store.dispatch('setDialog', dialogData)
        }
      } catch (err) {
        console.error('CATCH', this.$options.name, 'onCabinetOpen', err)
      }
    },

    sortTable(item: TableColumn): void {
      if (this.sort === item.value) {
        this.sort = '-' + item.value
      } else {
        this.sort = item.value
      }
    },

    setLoader(name: string, status: boolean): void {
      this.$store.dispatch('setLoader', {
        key: name,
        value: status
      })
    },
  },
})
</script>

<style>
</style>
